import { t } from "@lingui/macro";
import {
  type BudgetExportOptionsInput,
  BudgetPdfExportOurWorkPositionsEnum,
  BudgetPdfExportOurWorkViewEnum,
  type BudgetPdfSettingsFragment,
  BudgetTypeEnum,
  GetQuoteDocument,
  type GetQuoteQuery,
  type GetQuoteQueryVariables,
  PreviewQuoteDocument,
  type PreviewQuoteMutation,
  type PreviewQuoteMutationVariables,
  SaveQuoteDocument,
  type SaveQuoteMutation,
  type SaveQuoteMutationVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { toApiDate } from "@src/utils/dates";
import { denominate, nominate } from "@src/utils/formatters";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { addDays } from "date-fns";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

export const getTypeOptions = (): IOption[] => [
  { label: t`Active assigned`, value: BudgetTypeEnum.ActiveAssigned },
  { label: t`Active unassigned`, value: BudgetTypeEnum.Assigned },
  { label: t`Proposals`, value: BudgetTypeEnum.Unassigned },
  { label: t`Archived`, value: BudgetTypeEnum.Archived },
];

type Quote = GetQuoteQuery["GetQuote"];

export class EditQuotePageStore {
  @observable.ref quote: Quote | null = null;
  @observable.ref previewUrl: string | null = null;
  @observable.ref billingEntities: NonNullable<
    Quote["Client"]
  >["billingEntities"] = [];

  previewQuoteMutator = new MutationHelper<
    PreviewQuoteMutation,
    PreviewQuoteMutationVariables
  >(PreviewQuoteDocument);

  saveQuoteMutator = new MutationHelper<
    SaveQuoteMutation,
    SaveQuoteMutationVariables
  >(SaveQuoteDocument);

  quoteFetcher = new FetchHelper<GetQuoteQuery, GetQuoteQueryVariables>(
    GetQuoteDocument,
  );

  @observable.deep optionsForm: ReturnType<typeof this.getOptionsForm> | null =
    null;

  isLoadingSendQuote = new BooleanState(false);
  isDownloadingQuote = new BooleanState(false);

  constructor(private appStore: AppStore) {
    makeObservable(this);

    this.optionsForm = this.getOptionsForm(
      appStore.workspaceStore.settings?.budget_pdf_export_settings,
    );
  }

  @computed get hasMultipleCompanies() {
    if (!this.optionsForm?.$.workspace_company_id.value) return true;
    return this.appStore.workspaceStore.hasMultipleCompanies(
      this.optionsForm.$.workspace_company_id.value,
    );
  }

  @computed get hasMultipleBillingEntities() {
    return this.billingEntityOptions.length > 1;
  }

  @computed get hasMultipleBankAccounts() {
    if (!this.optionsForm?.$.workspace_company_id.value) return true;

    return this.appStore.workspaceStore.companyHasMultipleBankAccounts(
      this.optionsForm.$.workspace_company_id.value,
      this.optionsForm.$.workspace_bank_account_id.value,
    );
  }

  @computed
  get hasForeignCurrency() {
    return (
      this.appStore.workspaceStore.settings?.currency.id !==
      this.quote?.currency.id
    );
  }

  @computed
  get showExchangeRateInput() {
    return (
      this.hasForeignCurrency && this.optionsForm?.$.add_billing_plan.value
    );
  }

  @computed get showBillingSelects() {
    return (
      this.hasMultipleCompanies ||
      this.hasMultipleBillingEntities ||
      this.hasMultipleBankAccounts
    );
  }

  @computed get billingSelectsCount() {
    let count = 0;
    if (this.hasMultipleCompanies) count++;
    if (this.hasMultipleBillingEntities) count++;
    if (this.hasMultipleBankAccounts) count++;
    return count;
  }

  @computed get billingEntityOptions(): IOption[] {
    return mapToOptions.billingEntities.toOptions(this.billingEntities);
  }

  @computed get languageOptions(): IOption[] {
    return (
      this.appStore.workspaceStore.settings?.budget_xlsx_languages?.map(
        (language) => ({
          value: language,
          label: language,
        }),
      ) ?? [{ value: "EN", label: "EN" }]
    );
  }

  @computed get summaryData() {
    if (!this.quote) return undefined;
    const firstBudget = this.quote.budgets?.[0];

    if (!firstBudget) return undefined;

    return {
      brand: firstBudget.brand,
      project: firstBudget.project,
      budgetsIds: this.quote.budgets?.map((budget) => budget.id) ?? [],
      accountManager: firstBudget.account_manager?.split(",")?.[0],
      price: this.quote.total_amount,
      currency:
        this.quote.currency ?? this.appStore.workspaceStore.settings?.currency,
      sentToClientDate: this.quote.sent_to_client_date
        ? new Date(this.quote.sent_to_client_date)
        : undefined,
      clientApprovalDate: this.quote.client_approval_date
        ? new Date(this.quote.client_approval_date)
        : undefined,
      status: this.quote.status,
      date: this.quote.settings.date
        ? new Date(this.quote.settings.date)
        : undefined,
    };
  }

  @action.bound async fetchQuote(id: string) {
    const [data, error] = await this.quoteFetcher.fetch({
      id,
    });

    if (error || !data.GetQuote) return;

    this.quote = data.GetQuote;
    this.optionsForm = this.getOptionsForm(data.GetQuote.settings);
    this.previewUrl = data.GetQuote.url;

    if (
      !data.GetQuote.Client ||
      (data.GetQuote.settings.billing_entity_id &&
        !data.GetQuote.Client.billingEntities.find(
          (entity) => entity.id === data.GetQuote.settings.billing_entity_id,
        ))
    ) {
      // NOTE: In case settings are saved with entity ID but quote doesn't include saved entity ID
      this.optionsForm.$.billing_entity_id.reset(null);
    }

    if (!data.GetQuote.Client) return;

    if (
      !this.optionsForm.$.billing_entity_id.value &&
      data.GetQuote.Client.billingEntities.length === 1
    ) {
      this.optionsForm.$.billing_entity_id.onChange(
        data.GetQuote.Client.billingEntities[0].id ?? "",
      );
    }

    this.billingEntities = data.GetQuote.Client.billingEntities;
  }

  @action.bound async refreshPreview() {
    if (!this.quote) return;
    const settings = await this.serializeOptions();
    if (!settings) return;

    const [data, error] = await this.previewQuoteMutator.mutate({
      input: {
        id: this.quote.id,
        settings,
      },
    });

    if (error || !data.previewQuote) return;
    this.previewUrl = data.previewQuote.url;
  }

  getOptionsForm(defaultOptions: BudgetPdfSettingsFragment | undefined | null) {
    const defaultCompanyId =
      this.appStore.workspaceStore.defaultWorkspaceCompany?.id;
    const disclaimerMap = new Map<string, FieldState<string>>();

    this.languageOptions.forEach(({ value }) => {
      disclaimerMap.set(
        value,
        new FieldState(
          defaultOptions?.disclaimer?.find(
            (disclaimer) =>
              disclaimer?.language?.toUpperCase() === value.toUpperCase(),
          )?.disclaimer ?? "",
        ),
      );
    });

    return new FormState({
      date: new FieldState(new Date()),
      valid_for: new FieldState("30"),
      valid_until: new FieldState(addDays(new Date(), 30)),
      show_disclaimer: new FieldState(defaultOptions?.show_disclaimer),
      signature: new FieldState(defaultOptions?.signature),
      show_summary: new FieldState(defaultOptions?.show_summary),
      show_client_name: new FieldState(defaultOptions?.show_client_name),
      show_client_details: new FieldState(defaultOptions?.show_client_details),
      disclaimer: new FormState(disclaimerMap),

      billing_plan_exchange_rate: new FieldState(
        defaultOptions?.billing_plan_exchange_rate
          ? nominate(
              defaultOptions?.billing_plan_exchange_rate,
              EXCHANGE_RATE_DENOMINATION,
            )?.toString()
          : "1",
      ),
      our_work_positions: new FieldState(
        defaultOptions?.our_work_positions ??
          BudgetPdfExportOurWorkPositionsEnum.Columns,
      ),
      our_work_view: new FieldState(
        defaultOptions?.our_work_view ??
          BudgetPdfExportOurWorkViewEnum.Detailed,
      ),
      our_work_description: new FieldState(
        defaultOptions?.our_work_description,
      ),
      our_work_vat_in_items: new FieldState(
        defaultOptions?.our_work_vat_in_items,
      ),
      our_work_show_discount: new FieldState(
        defaultOptions?.our_work_show_discount,
      ),
      expense_show_commission: new FieldState(
        defaultOptions?.expense_show_commision,
      ),
      expense_show_description: new FieldState(
        defaultOptions?.expense_show_description,
      ),
      expense_show_quantity: new FieldState(
        defaultOptions?.expense_show_quantity,
      ),
      expense_show_unit: new FieldState(defaultOptions?.expense_show_unit),
      expense_show_discount: new FieldState(
        defaultOptions?.expense_show_discount,
      ),
      expense_show_vat_in_items: new FieldState(
        defaultOptions?.expense_show_vat_in_items,
      ),
      save_for_workspace: new FieldState(false),
      language: new FieldState(defaultOptions?.language ?? "EN"),
      show_hours: new FieldState(
        typeof defaultOptions?.show_hours === "boolean"
          ? defaultOptions.show_hours
          : true,
      ),
      title_page_with_vat: new FieldState(
        defaultOptions?.title_page_with_vat ?? false,
      ),
      workspace_company_id: new FieldState<string>(
        defaultOptions?.workspace_company_id ?? defaultCompanyId ?? "",
      ).validators(required),
      billing_entity_id: new FieldState(
        defaultOptions?.billing_entity_id ?? null,
      ),
      workspace_bank_account_id: new FieldState(
        defaultOptions?.workspace_bank_account_id ??
          this.appStore.workspaceStore.getDefaultCompanyBankAccount(
            defaultCompanyId,
          )?.id,
      ).validators(required),
      hide_title_page: new FieldState(defaultOptions?.hide_title_page),
      add_billing_plan: new FieldState(defaultOptions?.add_billing_plan),
      show_client_and_brand: new FieldState(
        defaultOptions?.show_client_and_brand,
      ),
      show_project_name_full: new FieldState(
        defaultOptions?.show_project_name_full,
      ),
      use_client_name_from_billing_entity: new FieldState(
        defaultOptions?.use_client_name_from_billing_entity,
      ),
    });
  }

  @action.bound async saveQuote() {
    if (!this.quote) return;
    const settings = await this.serializeOptions();
    if (!settings) return;

    const [data, error] = await this.saveQuoteMutator.mutate({
      input: {
        id: this.quote.id,
        settings,
      },
    });

    if (error || !data.saveQuote) return null;
    this.appStore.UIStore.toast({
      status: "success",
      title: t`Quote saved successfully.`,
    });
    this.previewUrl = null;
    // NOTE: To rerender PDF preview, we need to break this url set out of call stack, so JS can set
    // preview to null, and AFTER that, set it to the same url we received from API for it to
    // rerender with new saved PDF.
    setTimeout(() => {
      this.previewUrl = data.saveQuote.url;
    });
    return data.saveQuote;
  }

  @action.bound async sendQuote() {
    if (!this.quote) return;
    if ((await this.optionsForm?.validate())?.hasError) return;

    this.isLoadingSendQuote.on();
    await this.saveQuote();
    this.appStore.sendQuoteModalStore.modalState.onOpen({
      id: this.quote.id,
    });
    this.isLoadingSendQuote.off();
  }

  async serializeOptions(
    form = this.optionsForm,
  ): Promise<BudgetExportOptionsInput | undefined> {
    const validation = await form?.validate();
    if (!validation || validation.hasError) return;
    const data = validation.value;

    return {
      workspace_company_id: data.workspace_company_id.$,
      workspace_bank_account_id: data.workspace_bank_account_id.$,
      show_disclaimer: data.show_disclaimer.$,
      disclaimer: data.show_disclaimer.$
        ? Array.from(data.disclaimer.$.entries()).map(([key, value]) => ({
            language: key.toUpperCase(),
            disclaimer: value.$,
          }))
        : null,
      date: toApiDate(data.date.$),
      valid_until: toApiDate(addDays(data.date.$, Number(data.valid_for.$))),
      billing_entity_id: data.billing_entity_id.$,
      billing_plan_exchange_rate: data.billing_plan_exchange_rate.$
        ? denominate(
            this.showExchangeRateInput
              ? Number(data.billing_plan_exchange_rate.$)
              : 1,
            EXCHANGE_RATE_DENOMINATION,
          )
        : null,
      show_summary: data.show_summary.$,
      show_client_name: data.show_client_name.$,
      show_client_details: data.show_client_details.$,
      show_hours: data.show_hours.$,
      title_page_with_vat: data.title_page_with_vat.$,
      hide_title_page: data.hide_title_page.$,
      add_billing_plan: data.add_billing_plan.$,
      language: data.language.$,
      signature: data.signature.$,
      show_title_page_with_vat: data.title_page_with_vat.$,

      our_work_positions: data.our_work_positions.$,
      our_work_view: data.our_work_view.$,
      our_work_description: data.our_work_description.$,
      our_work_vat_in_items: data.our_work_vat_in_items.$,
      our_work_show_discount: data.our_work_show_discount.$,

      expense_show_commission: data.expense_show_commission.$,
      expense_show_description: data.expense_show_description.$,
      expense_show_quantity: data.expense_show_quantity.$,
      expense_show_unit: data.expense_show_unit.$,
      expense_show_discount: data.expense_show_discount.$,
      expense_show_vat_in_items: data.expense_show_vat_in_items.$,

      save_for_workspace: data.save_for_workspace.$,
      show_client_and_brand: data.show_client_and_brand.$,
      show_project_name_full: data.show_project_name_full.$,
      use_client_name_from_billing_entity:
        data.use_client_name_from_billing_entity.$,
    } as BudgetExportOptionsInput;
  }

  @action.bound async downloadQuote() {
    this.isDownloadingQuote.on();
    const quote = await this.saveQuote();
    this.isDownloadingQuote.off();

    if (!quote) return;
    window.open(quote.url, "_blank");
  }

  @action.bound handleOnSelectLegalEntity(id: string) {
    this.optionsForm?.$.workspace_bank_account_id.onChange(
      this.appStore.workspaceStore.getDefaultCompanyBankAccount(id)?.id,
    );
  }

  @action.bound handleSelectBillingEntity(id: string) {
    this.optionsForm?.$.billing_entity_id.onChange(id);
    const selectedEntity = this.billingEntities.find(
      (entity) => entity.id === id,
    );
    if (!selectedEntity || selectedEntity.workspaceCompany.deleted) {
      const defaultCompanyId =
        this.appStore.workspaceStore.defaultWorkspaceCompany?.id ?? "";
      this.optionsForm?.$.workspace_company_id.onChange(defaultCompanyId);
      this.handleOnSelectLegalEntity(defaultCompanyId);
      return;
    }

    this.optionsForm?.$.workspace_company_id.onChange(
      selectedEntity.workspaceCompany.id,
    );
    this.handleOnSelectLegalEntity(selectedEntity.workspaceCompany.id);
  }

  @computed
  get selectedLanguage() {
    return this.optionsForm?.$.language.value.toUpperCase();
  }

  @computed
  get disclaimerField() {
    if (!this.selectedLanguage) return undefined;
    return this.optionsForm?.$.disclaimer.$.get(this.selectedLanguage);
  }
}
